<template>
  <div
    class="wfb"
    :class="{ 'wfb--drag': dragDepth > 0 && canManage }"
    aria-label="Repozytorium plików bazy wiedzy"
    @dragover.prevent
    @dragenter.prevent="dragDepth++"
    @dragleave.prevent="dragDepth = Math.max(0, dragDepth - 1)"
    @drop.prevent="onDrop"
  >
    <div v-if="dragDepth > 0 && canManage" class="wfb-drop-overlay">
      <v-icon size="46" color="primary">mdi-tray-arrow-down</v-icon>
      <div>Upuść pliki, aby wgrać</div>
    </div>
    <section class="wfb-workspace">
      <!-- Pasek nawigacji i działań -->
      <header class="wfb-toolbar">
        <div class="wfb-toolbar__context">
          <v-btn
            v-if="breadcrumb.length"
            small
            outlined
            class="wfb-action wfb-back"
            aria-label="Wróć do folderu nadrzędnego"
            @click="goBack"
          >
            <v-icon small left>mdi-arrow-left</v-icon>
            Wstecz
          </v-btn>
          <div class="wfb-toolbar__label">
            <span class="wfb-toolbar__label-icon" aria-hidden="true">
              <v-icon size="18">mdi-folder-multiple-outline</v-icon>
            </span>
            <span>Repozytorium</span>
          </div>

          <nav class="wfb-breadcrumb" aria-label="Ścieżka folderów">
            <v-btn
              small
              text
              class="wfb-breadcrumb__button"
              :disabled="!breadcrumb.length"
              aria-label="Przejdź do głównego katalogu plików"
              @click="openFolder(null)"
            >
              <v-icon small left>mdi-home-outline</v-icon>
              Pliki
            </v-btn>
            <template v-for="(b, i) in breadcrumb">
              <v-icon :key="'s'+b.id" size="16" class="wfb-breadcrumb__separator" aria-hidden="true">
                mdi-chevron-right
              </v-icon>
              <v-btn
                :key="b.id"
                small
                text
                class="wfb-breadcrumb__button"
                :class="{ 'wfb-breadcrumb__button--current': i === breadcrumb.length - 1 }"
                :disabled="i === breadcrumb.length - 1"
                :aria-current="i === breadcrumb.length - 1 ? 'page' : null"
                @click="openFolder(b.id)"
              >{{ b.name }}</v-btn>
            </template>
          </nav>

          <div class="wfb-toolbar__summary" aria-live="polite">
            <span><strong>{{ folders.length }}</strong> {{ folders.length === 1 ? 'folder' : 'folderów' }}</span>
            <i aria-hidden="true" />
            <span><strong>{{ files.length }}</strong> {{ files.length === 1 ? 'plik' : 'plików' }}</span>
          </div>
        </div>

        <div v-if="canManage" class="wfb-toolbar__actions" aria-label="Zarządzanie plikami">
          <v-btn
            small
            outlined
            class="wfb-action wfb-action--note"
            aria-label="Utwórz nową notatkę"
            @click="openNote()"
          >
            <v-icon small left>mdi-note-edit-outline</v-icon>
            Nowa notatka
          </v-btn>
          <v-btn
            small
            outlined
            class="wfb-action wfb-action--folder"
            aria-label="Utwórz nowy folder"
            @click="newFolder"
          >
            <v-icon small left>mdi-folder-plus-outline</v-icon>
            Nowy folder
          </v-btn>
          <v-btn
            small
            class="wfb-action wfb-action--upload"
            :loading="uploading"
            aria-label="Wgraj pliki do bieżącego folderu"
            @click="$refs.fileInput.click()"
          >
            <v-icon small left>mdi-cloud-upload-outline</v-icon>
            Wgraj pliki
          </v-btn>
          <v-btn
            small
            class="wfb-action wfb-action--upload"
            :loading="uploading"
            aria-label="Wgraj pliki do bieżącego folderu"
            @click="$refs.dirInput.click()"
          >
            <v-icon small left>mdi-folder-upload</v-icon>Wgraj folder</v-btn>
          <input ref="fileInput" type="file" multiple class="d-none" @change="onFiles" />
          <input ref="dirInput" type="file" webkitdirectory class="d-none" @change="onDirPick" />
        </div>
      </header>

      <div v-if="loading" class="wfb-state wfb-state--loading" role="status" aria-live="polite">
        <div class="wfb-state__visual wfb-state__visual--loading" aria-hidden="true">
          <v-progress-circular indeterminate :size="34" :width="3" color="#6F8BFF" />
        </div>
        <div class="wfb-state__copy">
          <strong>Otwieramy repozytorium</strong>
          <span>Trwa pobieranie folderów i dokumentów…</span>
        </div>
      </div>

      <div v-else class="wfb-content">
        <div v-if="!folders.length && !files.length" class="wfb-state wfb-state--empty" role="status">
          <div class="wfb-state__visual" aria-hidden="true">
            <v-icon size="34">mdi-folder-open-outline</v-icon>
          </div>
          <div class="wfb-state__copy">
            <strong>Ten folder jest jeszcze pusty</strong>
            <span>{{ canManage ? 'Dodaj notatkę, utwórz folder albo wgraj pierwsze pliki.' : 'Nie dodano tu jeszcze żadnych materiałów.' }}</span>
          </div>
        </div>

        <section v-if="folders.length" class="wfb-section" aria-labelledby="wfb-folders-title">
          <div class="wfb-section__header">
            <div>
              <span class="wfb-section__eyebrow">Organizacja wiedzy</span>
              <h3 id="wfb-folders-title">Foldery</h3>
            </div>
            <span class="wfb-section__count">{{ folders.length }}</span>
          </div>

          <div class="wfb-grid wfb-grid--folders">
            <div
              v-for="f in folders"
              :key="'d'+f.id"
              class="wfb-item wfb-item--folder"
            >
              <button type="button" class="wfb-item__trigger" :aria-label="'Otwórz folder ' + f.name" @click="openFolder(f.id)">
                <span class="wfb-thumb wfb-thumb--folder" aria-hidden="true">
                  <v-icon size="34">mdi-folder</v-icon>
                </span>
                <span class="wfb-item__body">
                  <span class="wfb-item__type">Folder</span>
                  <span class="wfb-name" :title="f.name">{{ f.name }}</span>
                  <span class="wfb-item__hint">Otwórz zawartość</span>
                </span>
                <v-icon class="wfb-item__open" size="20" aria-hidden="true">mdi-chevron-right</v-icon>
              </button>
              <div v-if="canManage" class="wfb-actions" aria-label="Akcje folderu">
                <v-btn
                  icon
                  small
                  class="wfb-icon-action"
                  :aria-label="'Zmień nazwę folderu ' + f.name"
                  :title="'Zmień nazwę folderu ' + f.name"
                  @click.stop="renameFolder(f)"
                ><v-icon size="17">mdi-pencil-outline</v-icon></v-btn>
                <v-btn
                  icon
                  small
                  class="wfb-icon-action wfb-icon-action--danger"
                  :aria-label="'Usuń folder ' + f.name"
                  :title="'Usuń folder ' + f.name"
                  @click.stop="removeFolder(f)"
                ><v-icon size="17">mdi-trash-can-outline</v-icon></v-btn>
              </div>
            </div>
          </div>
        </section>

        <section v-if="files.length" class="wfb-section" aria-labelledby="wfb-files-title">
          <div class="wfb-section__header">
            <div>
              <span class="wfb-section__eyebrow">Materiały zespołu</span>
              <h3 id="wfb-files-title">Pliki i notatki</h3>
            </div>
            <span class="wfb-section__count">{{ files.length }}</span>
          </div>

          <div class="wfb-grid wfb-grid--files">
            <div
              v-for="f in files"
              :key="f.id"
              class="wfb-item wfb-item--file"
            >
              <button type="button" class="wfb-item__trigger" :aria-label="'Otwórz plik ' + f.name" @click="openFile(f)">
                <span class="wfb-thumb wfb-thumb--file" aria-hidden="true">
                  <img v-if="isImage(f) && thumbs[f.id]" :src="thumbs[f.id]" alt="" loading="lazy" />
                  <v-icon v-else-if="isImage(f)" size="30" color="#B794F6">mdi-image-outline</v-icon>
                  <v-icon v-else size="30" :color="iconColor(f)">{{ iconFor(f) }}</v-icon>
                </span>
                <span class="wfb-item__body">
                  <span class="wfb-item__type">{{ isText(f) ? 'Notatka' : (ext(f) || 'Plik') }}</span>
                  <span class="wfb-name" :title="f.name">{{ f.name }}</span>
                  <span class="wfb-meta">
                    <span>{{ f.fileSize || 'Rozmiar niedostępny' }}</span>
                    <i aria-hidden="true" />
                    <span>{{ isText(f) ? 'Edytowalna' : 'Podgląd pliku' }}</span>
                  </span>
                </span>
              </button>
              <div class="wfb-actions" aria-label="Akcje pliku">
                <v-btn
                  icon
                  small
                  class="wfb-icon-action"
                  :aria-label="'Pobierz plik ' + f.name"
                  :title="'Pobierz plik ' + f.name"
                  @click.stop="downloadFile(f)"
                ><v-icon size="17">mdi-download-outline</v-icon></v-btn>
                <template v-if="canManage">
                  <v-btn
                    icon
                    small
                    class="wfb-icon-action"
                    :aria-label="'Zmień nazwę pliku ' + f.name"
                    :title="'Zmień nazwę pliku ' + f.name"
                    @click.stop="renameFile(f)"
                  ><v-icon size="17">mdi-pencil-outline</v-icon></v-btn>
                  <v-btn
                    icon
                    small
                    class="wfb-icon-action wfb-icon-action--danger"
                    :aria-label="'Usuń plik ' + f.name"
                    :title="'Usuń plik ' + f.name"
                    @click.stop="removeFile(f)"
                  ><v-icon size="17">mdi-trash-can-outline</v-icon></v-btn>
                </template>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>

    <!-- Dialog notatki -->
    <v-dialog v-model="noteDialog" max-width="640" persistent>
      <v-card dark class="wfb-note-dialog">
        <div class="wfb-note-dialog__header">
          <div class="wfb-note-dialog__icon" aria-hidden="true">
            <v-icon size="21">mdi-note-edit-outline</v-icon>
          </div>
          <div class="wfb-note-dialog__heading">
            <span>{{ noteFile ? 'Notatka w repozytorium' : 'Nowy materiał' }}</span>
            <h2>{{ noteFile ? noteFile.name : 'Utwórz notatkę' }}</h2>
          </div>
          <v-btn icon small class="wfb-note-dialog__close" aria-label="Zamknij okno notatki" @click="noteDialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="wfb-note-dialog__body">
          <v-text-field
            v-if="!noteFile"
            v-model="noteName"
            label="Tytuł notatki"
            placeholder="np. Procedura obsługi zgłoszenia"
            prepend-inner-icon="mdi-format-title"
            dense
            outlined
          />
          <v-textarea
            v-model="noteContent"
            label="Treść notatki"
            placeholder="Wpisz czytelną instrukcję lub informację dla zespołu…"
            rows="12"
            outlined
            :readonly="!canManage"
          />
        </v-card-text>
        <v-card-actions class="wfb-note-dialog__actions">
          <span class="wfb-note-dialog__hint">
            <v-icon size="15">mdi-shield-check-outline</v-icon>
            {{ noteFile
              ? (canManage ? 'Zmiany zostaną zapisane w tym pliku' : 'Podgląd w trybie tylko do odczytu')
              : 'Materiał zapisze się w bieżącym folderze' }}
          </span>
          <v-spacer />
          <v-btn text class="wfb-dialog-button wfb-dialog-button--quiet" @click="noteDialog = false">Zamknij</v-btn>
          <v-btn v-if="canManage" class="wfb-dialog-button wfb-dialog-button--primary" :loading="noteSaving" @click="saveNote">
            <v-icon small left>mdi-content-save-outline</v-icon>
            Zapisz notatkę
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

const IMG_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];

export default {
  name: 'WikiFilesBrowser',
  props: { canManage: { type: Boolean, default: false } },
  data() {
    return {
      dragDepth: 0,
      loading: true,
      uploading: false,
      folderId: null,
      folders: [],
      files: [],
      breadcrumb: [],
      thumbs: {},
      noteDialog: false,
      noteFile: null,
      noteName: '',
      noteContent: '',
      noteSaving: false,
    };
  },
  async created() {
    await this.load();
  },
  methods: {
    getConfig() {
      return { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
    },
    async load() {
      this.loading = true;
      try {
        const q = this.folderId ? '?folderId=' + this.folderId : '';
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'wiki/files' + q, this.getConfig());
        const data = res.data?.data || res.data || {};
        this.folders = data.folders || [];
        this.files = data.files || [];
        this.breadcrumb = data.breadcrumb || [];
        this.loadThumbs();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      const parent = this.breadcrumb.length > 1 ? this.breadcrumb[this.breadcrumb.length - 2].id : null;
      this.openFolder(parent);
    },
    async openFolder(id) {
      this.folderId = id;
      await this.load();
    },
    ext(f) {
      return (f.name || '').split('.').pop().toLowerCase();
    },
    isImage(f) {
      return IMG_EXT.includes(this.ext(f));
    },
    async fetchBlob(f, download) {
      const res = await axios.get(
        process.env.VUE_APP_ROOT_API + `wiki/files/${f.id}/download${download ? '?download=1' : ''}`,
        { ...this.getConfig(), responseType: 'blob' },
      );
      return URL.createObjectURL(res.data);
    },
    async loadThumbs() {
      const imgs = this.files.filter((f) => this.isImage(f)).slice(0, 40);
      for (const f of imgs) {
        if (this.thumbs[f.id]) continue;
        try {
          const url = await this.fetchBlob(f);
          this.$set(this.thumbs, f.id, url);
        } catch (e) { /* miniatura opcjonalna */ }
      }
    },
    iconFor(f) {
      const e = this.ext(f);
      if (e === 'pdf') return 'mdi-file-pdf-box';
      if (['doc', 'docx', 'odt'].includes(e)) return 'mdi-file-word-box';
      if (['xls', 'xlsx', 'csv'].includes(e)) return 'mdi-file-excel-box';
      if (['zip', 'rar', '7z'].includes(e)) return 'mdi-folder-zip';
      if (['txt', 'md'].includes(e)) return 'mdi-file-document-outline';
      if (['mp4', 'mov', 'avi'].includes(e)) return 'mdi-file-video';
      if (['mp3', 'wav'].includes(e)) return 'mdi-file-music';
      return 'mdi-file-outline';
    },
    iconColor(f) {
      const e = this.ext(f);
      if (e === 'pdf') return 'red';
      if (['doc', 'docx', 'odt'].includes(e)) return 'blue';
      if (['xls', 'xlsx', 'csv'].includes(e)) return 'green';
      return 'blue-grey';
    },
    isText(f) {
      return ['txt', 'md'].includes(this.ext(f));
    },
    async openFile(f) {
      if (this.isText(f)) return this.openNote(f);
      try {
        const url = await this.fetchBlob(f);
        window.open(url, '_blank');
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się otworzyć pliku', 'error');
      }
    },
    async openNote(f) {
      this.noteFile = f || null;
      this.noteName = '';
      this.noteContent = '';
      if (f) {
        try {
          const res = await axios.get(
            process.env.VUE_APP_ROOT_API + `wiki/files/${f.id}/download`,
            { ...this.getConfig(), responseType: 'text' },
          );
          this.noteContent = typeof res.data === 'string' ? res.data : '';
        } catch (e) { /* pusta */ }
      }
      this.noteDialog = true;
    },
    async saveNote() {
      this.noteSaving = true;
      try {
        if (this.noteFile) {
          await axios.put(
            process.env.VUE_APP_ROOT_API + `wiki/files/${this.noteFile.id}/note`,
            { content: this.noteContent },
            this.getConfig(),
          );
        } else {
          if (!this.noteName.trim()) { Swal.fire('Błąd', 'Podaj tytuł notatki', 'error'); this.noteSaving = false; return; }
          await axios.post(
            process.env.VUE_APP_ROOT_API + 'wiki/files/note',
            { name: this.noteName, content: this.noteContent, folderId: this.folderId },
            this.getConfig(),
          );
        }
        this.noteDialog = false;
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się zapisać notatki', 'error');
      } finally {
        this.noteSaving = false;
      }
    },
    async downloadFile(f) {
      try {
        const url = await this.fetchBlob(f, true);
        const a = document.createElement('a');
        a.href = url; a.download = f.name; a.click();
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się pobrać pliku', 'error');
      }
    },
    async onDrop(e) {
      this.dragDepth = 0;
      if (!this.canManage) return;
      const items = [...(e.dataTransfer?.items || [])];
      const entries = items
        .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
        .filter(Boolean);
      if (entries.length) {
        this.uploading = true;
        try {
          await this.uploadEntries(entries, this.folderId);
          await this.load();
        } catch (err) {
          Swal.fire('Błąd', this.errMsg(err), 'error');
        } finally {
          this.uploading = false;
        }
        return;
      }
      const files = [...(e.dataTransfer?.files || [])];
      if (files.length) await this.doUpload(files);
    },
    // Rekurencyjnie: pliki wgrywa, dla folderów tworzy foldery i schodzi w głąb.
    async uploadEntries(entries, folderId) {
      const files = [];
      for (const entry of entries) {
        if (entry.isFile) {
          files.push(await new Promise((res, rej) => entry.file(res, rej)));
        } else if (entry.isDirectory) {
          const res = await axios.post(
            process.env.VUE_APP_ROOT_API + 'wiki/folders',
            { name: entry.name, parentId: folderId },
            this.getConfig(),
          );
          const newId = (res.data?.data || res.data || {}).id;
          const children = await this.readAllEntries(entry.createReader());
          await this.uploadEntries(children, newId);
        }
      }
      if (files.length) await this.uploadBatch(files, folderId);
    },
    readAllEntries(reader) {
      // readEntries zwraca porcjami po ~100 — czytamy do wyczerpania.
      return new Promise((resolve, reject) => {
        const all = [];
        const step = () => reader.readEntries((batch) => {
          if (!batch.length) return resolve(all);
          all.push(...batch);
          step();
        }, reject);
        step();
      });
    },
    async uploadBatch(files, folderId) {
      // Serwer przyjmuje max 20 plików na żądanie — wysyłamy porcjami.
      for (let i = 0; i < files.length; i += 15) {
        const chunk = files.slice(i, i + 15);
        const form = new FormData();
        chunk.forEach((f) => form.append('files', f));
        if (folderId) form.append('folderId', folderId);
        await axios.post(process.env.VUE_APP_ROOT_API + 'wiki/files/upload', form, this.getConfig());
      }
    },
    errMsg(e) {
      if (e?.response?.status === 413) return 'Plik jest za duży (limit 50 MB).';
      return e?.response?.data?.message || e?.message || 'Nie udało się wgrać plików';
    },
    async onFiles(ev) {
      const files = [...(ev.target.files || [])];
      ev.target.value = '';
      if (files.length) await this.doUpload(files);
    },
    async onDirPick(ev) {
      const files = [...(ev.target.files || [])];
      ev.target.value = '';
      if (!files.length) return;
      this.uploading = true;
      try {
        // webkitRelativePath: "NazwaFolderu/pod/plik.png" — odtwarzamy strukturę.
        const folderCache = {};
        const ensureFolder = async (pathParts) => {
          let parentId = this.folderId;
          let acc = '';
          for (const part of pathParts) {
            acc += '/' + part;
            if (!folderCache[acc]) {
              const res = await axios.post(
                process.env.VUE_APP_ROOT_API + 'wiki/folders',
                { name: part, parentId },
                this.getConfig(),
              );
              folderCache[acc] = (res.data?.data || res.data || {}).id;
            }
            parentId = folderCache[acc];
          }
          return parentId;
        };
        const groups = {};
        for (const f of files) {
          const parts = (f.webkitRelativePath || f.name).split('/');
          const dir = parts.slice(0, -1).join('/');
          (groups[dir] = groups[dir] || []).push(f);
        }
        for (const dir of Object.keys(groups)) {
          const target = dir ? await ensureFolder(dir.split('/')) : this.folderId;
          await this.uploadBatch(groups[dir], target);
        }
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', this.errMsg(e), 'error');
      } finally {
        this.uploading = false;
      }
    },
    async doUpload(files) {
      this.uploading = true;
      try {
        await this.uploadBatch(files, this.folderId);
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', this.errMsg(e), 'error');
      } finally {
        this.uploading = false;
      }
    },
    async newFolder() {
      const res = await Swal.fire({ title: 'Nazwa folderu', input: 'text', showCancelButton: true, confirmButtonText: 'Utwórz', cancelButtonText: 'Anuluj' });
      if (!res.isConfirmed || !res.value) return;
      try {
        await axios.post(process.env.VUE_APP_ROOT_API + 'wiki/folders', { name: res.value, parentId: this.folderId }, this.getConfig());
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się utworzyć folderu', 'error');
      }
    },
    async renameFolder(f) {
      const res = await Swal.fire({ title: 'Nowa nazwa', input: 'text', inputValue: f.name, showCancelButton: true });
      if (!res.isConfirmed || !res.value) return;
      await axios.put(process.env.VUE_APP_ROOT_API + 'wiki/folders/' + f.id, { name: res.value }, this.getConfig()).catch((e) => Swal.fire('Błąd', e.response?.data?.message || '', 'error'));
      await this.load();
    },
    async removeFolder(f) {
      const res = await Swal.fire({ title: `Usunąć folder „${f.name}"?`, text: 'Folder musi być pusty.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Usuń' });
      if (!res.isConfirmed) return;
      try {
        await axios.delete(process.env.VUE_APP_ROOT_API + 'wiki/folders/' + f.id, this.getConfig());
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się usunąć', 'error');
      }
    },
    async renameFile(f) {
      const res = await Swal.fire({ title: 'Nowa nazwa pliku', input: 'text', inputValue: f.name, showCancelButton: true });
      if (!res.isConfirmed || !res.value) return;
      await axios.put(process.env.VUE_APP_ROOT_API + 'wiki/files/' + f.id, { name: res.value }, this.getConfig()).catch((e) => Swal.fire('Błąd', e.response?.data?.message || '', 'error'));
      await this.load();
    },
    async removeFile(f) {
      const res = await Swal.fire({ title: `Usunąć plik „${f.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Usuń' });
      if (!res.isConfirmed) return;
      await axios.delete(process.env.VUE_APP_ROOT_API + 'wiki/files/' + f.id, this.getConfig()).catch((e) => Swal.fire('Błąd', e.response?.data?.message || '', 'error'));
      await this.load();
    },
  },
};
</script>

<style scoped>
.wfb { position: relative; }
.wfb--drag { outline: 2px dashed #1f6eb8; outline-offset: -2px; border-radius: 14px; }
.wfb-drop-overlay { position: absolute; inset: 0; z-index: 6; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; background: rgba(230, 240, 249, 0.92); border-radius: 14px; font-weight: 700; color: #1f6eb8; pointer-events: none; }

.wfb {
  --wfb-bg: #171B26;
  --wfb-surface: #232838;
  --wfb-surface-soft: #1F2434;
  --wfb-surface-hover: #292F40;
  --wfb-border: rgba(255, 255, 255, 0.075);
  --wfb-border-strong: rgba(126, 151, 255, 0.24);
  --wfb-text: #F5F7FC;
  --wfb-muted: #AEB8CA;
  --wfb-subtle: #9AA5B7;
  --wfb-blue: #5B7CFF;
  --wfb-blue-soft: rgba(91, 124, 255, 0.13);
  --wfb-danger: #FF8585;
  color: var(--wfb-text);
}

.wfb,
.wfb * { box-sizing: border-box; }

.wfb-workspace {
  overflow: hidden;
  border: 1px solid var(--wfb-border);
  border-radius: 22px;
  background:
    radial-gradient(circle at 90% -12%, rgba(91, 124, 255, 0.12), transparent 30%),
    var(--wfb-surface-soft);
  box-shadow: 0 28px 58px -48px rgba(0, 0, 0, 0.95);
}

.wfb-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--wfb-border);
  background: rgba(35, 40, 56, 0.82);
}

.wfb-toolbar__context {
  display: flex;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1 1 380px;
  align-items: center;
  gap: 10px 12px;
}
.wfb-toolbar__context .wfb-breadcrumb { flex: 1 1 160px; }
.wfb-back.v-btn { flex: 0 0 auto; }

.wfb-toolbar__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #D9DFEA;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}

.wfb-toolbar__label-icon {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 151, 255, 0.22);
  border-radius: 11px;
  background: linear-gradient(145deg, rgba(91, 124, 255, 0.22), rgba(91, 124, 255, 0.08));
}

.wfb-toolbar__label-icon >>> .v-icon { color: #8EA7FF !important; }

.wfb-breadcrumb {
  display: flex;
  min-width: 0;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
}

.wfb-breadcrumb::-webkit-scrollbar { display: none; }

.wfb-breadcrumb__button.v-btn {
  min-width: 0 !important;
  height: 32px !important;
  flex: 0 0 auto;
  padding: 0 8px !important;
  border-radius: 9px;
  color: #C8D0DE !important;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.wfb-breadcrumb__button.v-btn:hover { background: rgba(255, 255, 255, 0.055); }
.wfb-breadcrumb__button--current.v-btn { color: #FFFFFF !important; opacity: 1 !important; }
.wfb-breadcrumb__button >>> .v-icon { color: #8194D7 !important; }
.wfb-breadcrumb__separator { flex: 0 0 auto; color: #596377 !important; }

.wfb-toolbar__summary {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--wfb-subtle);
  font-size: 11px;
  white-space: nowrap;
}

.wfb-toolbar__summary strong { color: #DCE2EC; font-weight: 800; }
.wfb-toolbar__summary i,
.wfb-meta i {
  width: 3px;
  height: 3px;
  flex: 0 0 3px;
  border-radius: 50%;
  background: #5B6578;
}

.wfb-toolbar__actions {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
}

.wfb-action.v-btn {
  height: 38px !important;
  padding: 0 12px !important;
  border-radius: 11px;
  color: #DCE2EC !important;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.wfb-action--note.v-btn,
.wfb-action--folder.v-btn {
  border-color: rgba(255, 255, 255, 0.11) !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.wfb-action--note.v-btn:hover,
.wfb-action--folder.v-btn:hover {
  border-color: rgba(142, 167, 255, 0.28) !important;
  background: rgba(91, 124, 255, 0.09) !important;
}

.wfb-action--upload.v-btn {
  border: 1px solid #5B7CFF !important;
  background: #5B7CFF !important;
  color: #FFFFFF !important;
  box-shadow: 0 11px 26px -17px rgba(91, 124, 255, 0.96);
}

.wfb-action--upload.v-btn:hover { background: #6B88FF !important; }
.wfb-action >>> .v-icon { color: inherit !important; }

.wfb-content { padding: 20px; }
.wfb-section + .wfb-section { margin-top: 28px; }

.wfb-section__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  padding: 0 2px;
}

.wfb-section__eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #8591A7;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.wfb-section__header h3 {
  margin: 0;
  color: #F7F8FC;
  font-size: 17px;
  font-weight: 900;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.wfb-section__count {
  display: inline-flex;
  min-width: 28px;
  height: 25px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border: 1px solid var(--wfb-border);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.045);
  color: #AFB8C9;
  font-size: 11px;
  font-weight: 800;
}

.wfb-grid {
  display: grid;
  gap: 10px;
}

.wfb-grid--folders { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
.wfb-grid--files { grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); }

.wfb-item {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 92px;
  align-items: center;
  gap: 12px;
  padding: 12px;
  overflow: hidden;
  border: 1px solid var(--wfb-border);
  border-radius: 15px;
  background: rgba(35, 40, 56, 0.88);
  text-align: left;
  transition: border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.wfb-item__trigger {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
  gap: 12px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.wfb-item::before {
  position: absolute;
  top: 0;
  right: 18px;
  left: 18px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  content: '';
}

.wfb-item:hover,
.wfb-item:focus-within {
  border-color: var(--wfb-border-strong);
  background: var(--wfb-surface-hover);
  box-shadow: 0 17px 32px -28px rgba(0, 0, 0, 0.95);
  transform: translateY(-1px);
}

.wfb-item__trigger:focus-visible { outline: none; }
.wfb-item:focus-within { box-shadow: 0 0 0 2px #8EA7FF, 0 17px 32px -28px rgba(0, 0, 0, 0.95); }

.wfb-thumb {
  display: flex;
  width: 60px;
  height: 60px;
  flex: 0 0 60px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 13px;
}

.wfb-thumb--folder {
  border: 1px solid rgba(91, 124, 255, 0.18);
  background: linear-gradient(145deg, rgba(91, 124, 255, 0.18), rgba(91, 124, 255, 0.07));
}

.wfb-thumb--folder >>> .v-icon { color: #7896FF !important; }
.wfb-thumb--file { border: 1px solid rgba(255, 255, 255, 0.07); background: #1B202E; }

.wfb-thumb img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.wfb-item__body {
  min-width: 0;
  flex: 1 1 auto;
}

.wfb-item__type {
  display: block;
  margin-bottom: 3px;
  color: #9AA5B8;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}

.wfb-name {
  overflow: hidden;
  color: #F1F4F9;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wfb-item__hint,
.wfb-meta {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 5px;
  overflow: hidden;
  color: var(--wfb-subtle);
  font-size: 10.5px;
  line-height: 1.25;
  white-space: nowrap;
}

.wfb-item__open {
  flex: 0 0 auto;
  margin-left: 2px;
  color: #647087 !important;
  transition: color 0.16s ease, transform 0.16s ease;
}

.wfb-item:hover .wfb-item__open { color: #8EA7FF !important; transform: translateX(2px); }

.wfb-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 3px;
  padding: 3px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 10px;
  background: rgba(23, 27, 38, 0.78);
  opacity: 0.72;
  transition: border-color 0.16s ease, opacity 0.16s ease;
}

.wfb-item:hover .wfb-actions,
.wfb-item:focus-within .wfb-actions { border-color: rgba(142, 167, 255, 0.17); opacity: 1; }

.wfb-icon-action.v-btn {
  width: 30px !important;
  height: 30px !important;
  color: #AAB4C5 !important;
}

.wfb-icon-action.v-btn:hover { background: rgba(91, 124, 255, 0.12); color: #AFC0FF !important; }
.wfb-icon-action--danger.v-btn { color: #D98C91 !important; }
.wfb-icon-action--danger.v-btn:hover { background: rgba(255, 115, 115, 0.11); color: #FF9C9C !important; }
.wfb-icon-action >>> .v-icon { color: inherit !important; }

.wfb-state {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 44px 20px;
  text-align: left;
}

.wfb-state--loading { min-height: 330px; }

.wfb-state__visual {
  display: flex;
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 151, 255, 0.2);
  border-radius: 18px;
  background: var(--wfb-blue-soft);
}

.wfb-state__visual >>> .v-icon { color: #809AFF !important; }
.wfb-state__visual--loading { background: rgba(91, 124, 255, 0.08); }

.wfb-state__copy strong {
  display: block;
  color: #F4F6FA;
  font-size: 14px;
  font-weight: 900;
}

.wfb-state__copy span {
  display: block;
  max-width: 390px;
  margin-top: 4px;
  color: var(--wfb-muted);
  font-size: 12px;
  line-height: 1.45;
}

/* Dialog notatki */
.wfb-note-dialog.v-card {
  overflow: hidden;
  border: 1px solid rgba(142, 167, 255, 0.18);
  border-radius: 22px !important;
  background:
    radial-gradient(circle at 92% -20%, rgba(91, 124, 255, 0.17), transparent 38%),
    #1F2434 !important;
  color: #F5F7FC;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.48) !important;
}

.wfb-note-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.wfb-note-dialog__icon {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 151, 255, 0.22);
  border-radius: 13px;
  background: rgba(91, 124, 255, 0.14);
}

.wfb-note-dialog__icon >>> .v-icon { color: #8EA7FF !important; }
.wfb-note-dialog__heading { min-width: 0; }

.wfb-note-dialog__heading span {
  display: block;
  color: #8E9AAF;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.085em;
  text-transform: uppercase;
}

.wfb-note-dialog__heading h2 {
  margin: 3px 0 0;
  overflow: hidden;
  color: #FFFFFF;
  font-size: 19px;
  font-weight: 900;
  letter-spacing: -0.025em;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wfb-note-dialog__close { margin-left: auto; color: #AEB8CA !important; }
.wfb-note-dialog__body { padding: 22px 22px 4px !important; }

.wfb-note-dialog__body >>> .v-input { color: #E9EDF5 !important; }
.wfb-note-dialog__body >>> .v-input__slot { background: rgba(23, 27, 38, 0.64) !important; }
.wfb-note-dialog__body >>> fieldset { border-color: rgba(255, 255, 255, 0.12) !important; }
.wfb-note-dialog__body >>> .v-input--is-focused fieldset { border-color: #6F8BFF !important; }
.wfb-note-dialog__body >>> .v-label { color: #9DA8BA !important; }
.wfb-note-dialog__body >>> input,
.wfb-note-dialog__body >>> textarea { color: #F3F5F9 !important; caret-color: #8EA7FF !important; }
.wfb-note-dialog__body >>> .v-icon { color: #7485BE !important; }

.wfb-note-dialog__actions {
  min-height: 72px;
  padding: 12px 22px 18px !important;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.wfb-note-dialog__hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #9AA5B7;
  font-size: 10.5px;
}

.wfb-note-dialog__hint >>> .v-icon { color: #7088DB !important; }

.wfb-dialog-button.v-btn {
  min-height: 38px;
  border-radius: 10px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.wfb-dialog-button--quiet.v-btn { color: #B4BDCC !important; }
.wfb-dialog-button--primary.v-btn { background: #5B7CFF !important; color: #FFFFFF !important; }
.wfb-dialog-button--primary >>> .v-icon { color: #FFFFFF !important; }


.wfb-item__trigger { min-width: 0; }
.wfb-item__body { min-width: 0; }
.wfb-name { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

@media (max-width: 1120px) {
  .wfb-toolbar { align-items: stretch; flex-direction: column; gap: 12px; }
  .wfb-toolbar__actions { justify-content: flex-end; }
}

@media (max-width: 720px) {
  .wfb-workspace { border-radius: 17px; }
  .wfb-toolbar { padding: 14px; }
  .wfb-toolbar__context { grid-template-columns: minmax(0, 1fr) auto; }
  .wfb-toolbar__label { grid-column: 1 / 2; }
  .wfb-breadcrumb { grid-column: 1 / -1; grid-row: 2; margin: 0 -4px; }
  .wfb-toolbar__summary { grid-column: 2; grid-row: 1; }
  .wfb-toolbar__actions { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .wfb-action.v-btn { width: 100%; padding: 0 8px !important; }
  .wfb-content { padding: 16px 14px 18px; }
  .wfb-grid { grid-template-columns: 1fr; }
}

@media (max-width: 480px) {
  .wfb-toolbar__actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .wfb-action--upload.v-btn { grid-column: 1 / -1; }
  .wfb-toolbar__summary { gap: 5px; font-size: 10px; }
  .wfb-toolbar__label > span:last-child { display: none; }
  .wfb-item { min-height: 86px; gap: 10px; padding: 10px; }
  .wfb-item__trigger { gap: 10px; }
  .wfb-thumb { width: 54px; height: 54px; flex-basis: 54px; }
  .wfb-actions { gap: 1px; padding: 2px; opacity: 1; }
  .wfb-icon-action.v-btn { width: 32px !important; height: 32px !important; }
  .wfb-item__open { display: none; }
  .wfb-meta span:last-child,
  .wfb-meta i { display: none; }
  .wfb-state { min-height: 230px; align-items: center; flex-direction: column; text-align: center; }
  .wfb-note-dialog__header { padding: 17px; }
  .wfb-note-dialog__body { padding: 18px 17px 2px !important; }
  .wfb-note-dialog__actions { align-items: stretch; flex-wrap: wrap; padding: 12px 17px 17px !important; }
  .wfb-note-dialog__hint { width: 100%; margin-bottom: 4px; }
}

@media (hover: none) {
  .wfb-actions { opacity: 1; }
  .wfb-item:hover { transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .wfb-item,
  .wfb-item__open,
  .wfb-actions { transition: none; }
}
</style>
